import { useCallback, useEffect, useRef, useState } from 'react';
import type { OnboardingStep, SpotlightPosition } from '../model/types';
import { useInterfaceStore } from '@/store/useInterfaceStore';
import { isTabBarShown } from '@/features/native-app/widget/tabBarShown';
import { findAppShellTarget, findVisibleTarget } from './spotlightTarget';
import { isPhoneViewport } from './viewport';

const MAX_RETRIES = 15;
const RETRY_DELAY_MS = 200;
const SIDEBAR_OPEN_DELAY_MS = 350;
const SPOTLIGHT_PADDING = 8;

interface UseSpotlightTrackerArgs {
  currentStep: OnboardingStep | null;
  isVisible: boolean;
  isReady: boolean;
  setSpotlightPosition: (pos: SpotlightPosition | null) => void;
}

interface UseSpotlightTrackerResult {
  targetElement: Element | null;
  elementFound: boolean;
}

// The season selector lives in the sidebar too: on a phone or tablet the drawer must open first,
// otherwise the translated-away element still has a size and the bubble lands off screen.
export const isSidebarElement = (selector: string): boolean =>
  selector.includes('nav-') || selector.includes('sidebar') || selector.includes('season-selector');

/**
 * Off screen, or on a phone in the lower half where the bottom bubble would cover it:
 * bring the target up once per step (later scrolls are the user's).
 */
export function needsScroll(rect: Pick<DOMRect, 'top' | 'bottom'>): boolean {
  const limit = isPhoneViewport() ? window.innerHeight / 2 : window.innerHeight;
  return rect.top < 0 || rect.bottom > limit;
}

export function isSamePosition(
  a: SpotlightPosition | null | undefined,
  b: SpotlightPosition | null,
): boolean {
  if (a === undefined) return false;
  if (a === null || b === null) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function scrollableAncestor(element: Element): Element {
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    if (/(auto|scroll)/.test(window.getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return document.scrollingElement ?? document.documentElement;
}

/** On a phone the target goes to the upper quarter, clear of the bottom panel; elsewhere centred. */
export function scrollTargetIntoView(element: Element): void {
  if (!isPhoneViewport()) {
    element.scrollIntoView({ block: 'center' });
    return;
  }
  const rect = element.getBoundingClientRect();
  scrollableAncestor(element).scrollBy({ top: rect.top - window.innerHeight / 4 });
}

export const useSpotlightTracker = ({
  currentStep,
  isVisible,
  isReady,
  setSpotlightPosition,
}: UseSpotlightTrackerArgs): UseSpotlightTrackerResult => {
  const setSidebarOpen = useInterfaceStore((s) => s.setSidebarOpen);
  const sidebar = useInterfaceStore((s) => s.sidebar);

  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const [elementFound, setElementFound] = useState(false);

  const observerRef = useRef<MutationObserver | null>(null);
  const rafRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const isActiveRef = useRef(false);
  const scrolledTargetRef = useRef<Element | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestUpdateRef = useRef<() => void>(() => undefined);
  const lastPositionRef = useRef<SpotlightPosition | null | undefined>(undefined);

  // Every DOM mutation re-measures: publishing an equal position as a new object would re-render
  // the bubble, whose own DOM work would trigger the observer again, endlessly.
  const publishPosition = useCallback(
    (next: SpotlightPosition | null) => {
      if (isSamePosition(lastPositionRef.current, next)) return;
      lastPositionRef.current = next;
      setSpotlightPosition(next);
    },
    [setSpotlightPosition],
  );

  const updateSpotlightPosition = useCallback(() => {
    if (!isActiveRef.current) return;

    if (!currentStep) {
      publishPosition(null);
      setElementFound(false);
      return;
    }

    if (!currentStep.targetSelector || currentStep.placement === 'center') {
      // A drawer left open by the previous step would clutter the screen behind the message.
      if (sidebar.isOpen && !sidebar.isDesktop) setSidebarOpen(false);
      publishPosition(null);
      setElementFound(true);
      setTargetElement(null);
      return;
    }

    const isSidebarStep = isSidebarElement(currentStep.targetSelector);
    // The tab bar and « Plus » screen stand for the side menu, which never opens there.
    const inAppShell = isTabBarShown();
    const shell = isSidebarStep && inAppShell ? findAppShellTarget(currentStep.targetSelector) : null;

    if (sidebar.isOpen && (shell || (!isSidebarStep && !sidebar.isDesktop))) {
      // The drawer opened for a previous step would cover the tab bar or the page.
      setSidebarOpen(false);
    } else if (isSidebarStep && !inAppShell && !sidebar.isOpen) {
      setSidebarOpen(true);
      setTimeout(() => latestUpdateRef.current(), SIDEBAR_OPEN_DELAY_MS);
      return;
    }

    const element = isSidebarStep && inAppShell
      ? (shell?.element ?? null)
      : findVisibleTarget(currentStep.targetSelector);
    setTargetElement(element);

    if (element) {
      // Per element, not per step: a page change remounts the sidebar with its scroll reset.
      if (!shell?.inTabBar && scrolledTargetRef.current !== element) {
        scrolledTargetRef.current = element;
        if (needsScroll(element.getBoundingClientRect())) scrollTargetIntoView(element);
      }
      const rect = element.getBoundingClientRect();

      publishPosition({
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
      });
      setElementFound(true);
      retryCountRef.current = 0;
    } else {
      publishPosition(null);
      setElementFound(false);

      // One pending retry at a time: the sidebar's entry animations fire bursts of mutations,
      // which used to spend the whole budget before the drawer had even slid in.
      if (retryTimerRef.current === null && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          latestUpdateRef.current();
        }, RETRY_DELAY_MS);
      }
    }
  }, [currentStep, publishPosition, sidebar.isOpen, sidebar.isDesktop, setSidebarOpen]);

  useEffect(() => {
    latestUpdateRef.current = updateSpotlightPosition;
  }, [updateSpotlightPosition]);

  useEffect(() => {
    isActiveRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    retryCountRef.current = 0;
    lastPositionRef.current = undefined;
    setElementFound(false);
    setTargetElement(null);
  }, [currentStep?.id]);

  useEffect(
    () => () => {
      if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!isVisible || !currentStep || !isReady) return;

    retryCountRef.current = 0;
    updateSpotlightPosition();

    const handleScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateSpotlightPosition);
    };

    const handleResize = () => {
      updateSpotlightPosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    observerRef.current = new MutationObserver(() => {
      updateSpotlightPosition();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      observerRef.current?.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible, currentStep, isReady, updateSpotlightPosition]);

  return { targetElement, elementFound };
};
